import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Instagram, Loader2, CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";

export default function ConnectInstagram() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const done = params.get("done");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/public/connect-link/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ valid: false, status: "invalid" }))
      .finally(() => setLoading(false));
  }, [token]);

  const start = async () => {
    setConnecting(true);
    setErr("");
    try {
      const { data } = await api.get(`/public/instagram/connect/${token}`);
      window.location.href = data.authorize_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Impossible de démarrer la connexion.");
      setConnecting(false);
    }
  };

  const success = done === "1";
  const noAccount = done === "no_account";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Logo />
        </div>

        <div className="border border-white/10 rounded-sm bg-secondary p-8" data-testid="public-connect-card">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : success ? (
            <div className="text-center" data-testid="public-connect-success">
              <div className="h-14 w-14 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">Votre compte Instagram est connecté ✅</h1>
              <p className="text-sm text-muted-foreground">
                Merci ! {info?.brand_name ? <b className="text-foreground">{info.brand_name}</b> : "Votre marque"} est
                maintenant relié(e) à NATYE. Vous pouvez fermer cette page.
              </p>
            </div>
          ) : noAccount ? (
            <div className="text-center" data-testid="public-connect-noaccount">
              <div className="h-14 w-14 rounded-sm bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="h-7 w-7 text-amber-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">Aucun compte professionnel trouvé</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Assurez-vous que votre compte Instagram est un compte <b>Professionnel</b> ou <b>Créateur</b>, puis
                réessayez.
              </p>
              <button
                onClick={start}
                disabled={connecting}
                className="w-full bg-[#1877F2] text-white rounded-sm px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#1877F2]/85 transition-colors disabled:opacity-50"
              >
                Réessayer
              </button>
            </div>
          ) : !info?.valid ? (
            <div className="text-center" data-testid="public-connect-invalid">
              <div className="h-14 w-14 rounded-sm bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="h-7 w-7 text-red-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">
                {info?.status === "expired"
                  ? "Lien expiré"
                  : info?.status === "used"
                  ? "Lien déjà utilisé"
                  : "Lien invalide"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {info?.status === "used"
                  ? "Ce compte a déjà été connecté. Contactez votre agence si besoin d'un nouveau lien."
                  : "Ce lien n'est plus valide. Demandez un nouveau lien de connexion à votre agence."}
              </p>
            </div>
          ) : (
            <div data-testid="public-connect-start">
              <div className="h-14 w-14 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-6">
                <Instagram className="h-7 w-7 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-center mb-2">
                Connectez votre compte Instagram
              </h1>
              {info?.brand_name && (
                <p className="text-center text-sm text-muted-foreground mb-5">
                  pour la marque <b className="text-foreground">{info.brand_name}</b>
                </p>
              )}
              <p className="text-xs text-muted-foreground leading-relaxed mb-6 text-center">
                Cette connexion permet à NATYE de préparer et publier les contenus Instagram de votre marque.
                Votre mot de passe Instagram n'est <b>jamais</b> transmis à NATYE — tout passe par la page
                officielle et sécurisée d'Instagram.
              </p>
              {err && <p className="text-xs text-red-400 mb-4 text-center">{err}</p>}
              <button
                onClick={start}
                disabled={connecting}
                data-testid="public-connect-button"
                className="w-full flex items-center justify-center gap-2 bg-[#1877F2] text-white rounded-sm px-4 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#1877F2]/85 transition-colors disabled:opacity-50"
              >
                {connecting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Instagram className="h-5 w-5" />}
                Continuer avec Instagram
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground mt-4 uppercase tracking-wider">
                <ShieldCheck className="h-3.5 w-3.5" /> Connexion officielle & sécurisée Meta
              </p>
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-muted-foreground mt-6 uppercase tracking-[0.2em]">
          Propulsé par NATYE
        </p>
      </div>
    </div>
  );
}
